import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  App as AntApp,
} from "antd";
import {
  DeleteOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { tasksApi } from "../api/tasks";
import { catalogApi } from "../api/catalog";
import { usersApi } from "../api/users";
import type { Shop, Task, TaskPriority, TaskStatus, User } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { formatDateTimeIST } from "../utils/format";

const STATUS_TABS: { label: string; value: TaskStatus | "all" }[] = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "In Progress", value: "in_progress" },
  { label: "Completed", value: "completed" },
];

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  high: "red",
  medium: "gold",
  low: "green",
};

const STATUS_COLOR: Record<TaskStatus, string> = {
  pending: "default",
  in_progress: "blue",
  completed: "green",
};

const STATUS_LABEL: Record<TaskStatus, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
};

export function Tasks() {
  const { notification, modal } = AntApp.useApp();
  const [rows, setRows] = useState<Task[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<TaskStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, page_size: pageSize };
      if (status !== "all") params.status = status;
      if (search) params.search = search;
      const res = await tasksApi.list(params);
      setRows(res.results);
      setTotal(res.count);
    } finally {
      setLoading(false);
    }
  }, [status, search, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    usersApi
      .list({ role: "employee", is_active: true, page_size: 500 })
      .then((res) => setEmployees(res.results))
      .catch(() => setEmployees([]));
    catalogApi
      .listShops({ page_size: 500 })
      .then((res) => setShops(res.results))
      .catch(() => setShops([]));
  }, []);

  const employeeOptions = useMemo(
    () => employees.map((e) => ({ label: e.full_name, value: e.id, email: e.email })),
    [employees],
  );
  const shopOptions = useMemo(
    () => shops.map((s) => ({ label: s.area ? `${s.name} — ${s.area}` : s.name, value: s.id })),
    [shops],
  );

  const onAdd = async () => {
    try {
      const vals = await form.validateFields();
      const created = await tasksApi.create({
        employee_id: vals.employee_id,
        shop_ids: vals.shop_ids,
        title: vals.title,
        description: vals.description || "",
        priority: vals.priority,
        assigned_date: vals.assigned_date ? dayjs(vals.assigned_date).format("YYYY-MM-DD") : undefined,
        due_date: vals.due_date ? dayjs(vals.due_date).format("YYYY-MM-DD") : null,
      });
      notification.success({
        message: created.length === 1 ? "Task assigned" : `${created.length} tasks assigned`,
      });
      setAddOpen(false);
      form.resetFields();
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, unknown> }; errorFields?: unknown };
      if (err.errorFields) return; // form validation already shown
      const data = err.response?.data;
      const msg =
        (data && typeof data.detail === "string" ? data.detail : null) || "Could not assign task";
      notification.error({ message: msg });
    }
  };

  const changeStatus = async (task: Task, next: TaskStatus) => {
    try {
      await tasksApi.setStatus(task.id, next);
      notification.success({ message: `Marked ${STATUS_LABEL[next].toLowerCase()}` });
      load();
    } catch {
      notification.error({ message: "Could not update status" });
    }
  };

  const onDelete = (task: Task) => {
    modal.confirm({
      title: "Delete this task?",
      content: `"${task.title}" for ${task.shop_name} will be removed.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        await tasksApi.remove(task.id);
        notification.success({ message: "Task deleted" });
        load();
      },
    });
  };

  return (
    <div className="page-shell">
      <PageHeader
        title="Task Management"
        subtitle="Assign shop-visit tasks to your employees and track completion"
        right={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Assign Task
          </Button>
        }
      />

      <div className="toolbar-row">
        <Radio.Group
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          optionType="button"
        >
          {STATUS_TABS.map((t) => (
            <Radio.Button key={t.value} value={t.value}>
              {t.label}
            </Radio.Button>
          ))}
        </Radio.Group>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search title, shop, employee…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: 320 }}
        />
        <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--text-secondary)" }}>
          {total} total
        </div>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        columns={[
          { title: "Employee", dataIndex: "employee_name" },
          {
            title: "Shop",
            dataIndex: "shop_name",
            render: (v: string, row: Task) => (
              <div>
                <div>{v}</div>
                {row.shop_area && <div className="small-muted">{row.shop_area}</div>}
              </div>
            ),
          },
          {
            title: "Task",
            dataIndex: "title",
            render: (v: string, row: Task) => (
              <div>
                <b>{v}</b>
                {row.description && (
                  <div className="small-muted" style={{ maxWidth: 280 }}>
                    {row.description}
                  </div>
                )}
              </div>
            ),
          },
          {
            title: "Priority",
            dataIndex: "priority",
            render: (v: TaskPriority) => (
              <Tag color={PRIORITY_COLOR[v]} style={{ textTransform: "capitalize" }}>
                {v}
              </Tag>
            ),
          },
          {
            title: "Assigned",
            dataIndex: "assigned_date",
            render: (v: string) => <span className="small-muted">{v || "—"}</span>,
          },
          {
            title: "Due",
            dataIndex: "due_date",
            render: (v: string | null) =>
              v ? <span className="small-muted">{v}</span> : <span className="small-muted">—</span>,
          },
          {
            title: "Status",
            dataIndex: "status",
            render: (v: TaskStatus, row: Task) => (
              <Tooltip title={row.completed_at ? `Completed ${formatDateTimeIST(row.completed_at)}` : undefined}>
                <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v]}</Tag>
              </Tooltip>
            ),
          },
          {
            title: "",
            key: "_actions",
            width: 60,
            render: (_: unknown, row: Task) => (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "pending",
                      label: "Mark Pending",
                      disabled: row.status === "pending",
                      onClick: () => changeStatus(row, "pending"),
                    },
                    {
                      key: "in_progress",
                      label: "Mark In Progress",
                      disabled: row.status === "in_progress",
                      onClick: () => changeStatus(row, "in_progress"),
                    },
                    {
                      key: "completed",
                      label: "Mark Completed",
                      disabled: row.status === "completed",
                      onClick: () => changeStatus(row, "completed"),
                    },
                    { type: "divider" },
                    {
                      key: "delete",
                      label: "Delete",
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => onDelete(row),
                    },
                  ],
                }}
              >
                <Button type="text" icon={<MoreOutlined />} />
              </Dropdown>
            ),
          },
        ]}
      />

      <Modal
        title="Assign Task"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={onAdd}
        okText="Assign"
        destroyOnClose
      >
        <Form
          layout="vertical"
          form={form}
          requiredMark={false}
          preserve={false}
          initialValues={{ priority: "medium", assigned_date: dayjs() }}
        >
          <Form.Item label="Employee" name="employee_id" rules={[{ required: true, message: "Select an employee" }]}>
            <Select
              showSearch
              placeholder={employees.length === 0 ? "No active employees" : "Search employee"}
              optionFilterProp="label"
              filterOption={(input, option) => {
                const q = input.toLowerCase();
                return (
                  String(option?.label || "").toLowerCase().includes(q) ||
                  String((option as { email?: string })?.email || "").toLowerCase().includes(q)
                );
              }}
              options={employeeOptions}
            />
          </Form.Item>
          <Form.Item
            label="Shops to visit"
            name="shop_ids"
            rules={[{ required: true, message: "Select at least one shop" }]}
            extra="One task is created per selected shop."
          >
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select one or more shops"
              optionFilterProp="label"
              options={shopOptions}
            />
          </Form.Item>
          <Form.Item label="Title" name="title" rules={[{ required: true, max: 255 }]}>
            <Input placeholder="e.g. Collect outstanding payment" />
          </Form.Item>
          <Form.Item label="Instructions" name="description">
            <Input.TextArea rows={3} placeholder="Optional notes for the employee" />
          </Form.Item>
          <Space style={{ width: "100%" }} align="start">
            <Form.Item label="Priority" name="priority" style={{ flex: 1, minWidth: 130 }}>
              <Select
                options={[
                  { label: "Low", value: "low" },
                  { label: "Medium", value: "medium" },
                  { label: "High", value: "high" },
                ]}
              />
            </Form.Item>
            <Form.Item label="Assigned date" name="assigned_date" style={{ flex: 1 }}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Due date" name="due_date" style={{ flex: 1 }}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
